import { useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { Button, Checkbox, Divider, Dropdown, Flex } from 'antd';
import Icon, { FilterFilled } from '@ant-design/icons';

import { styledWithTransientProps } from 'utilities/styles';

const Trigger = styledWithTransientProps(Button)(({ active }) => {
  if (!active) {
    return `
      &.ant-btn-color-link.ant-btn-variant-link {
        color: var(--neutral-4);

        &:not(:disabled):not(.ant-btn-disabled):hover {
          color: var(--neutral-5);
        }
      }
    `;
  }
});

const MultiSelectDropdown = ({
  items = [],
  onChange = () => {},
  triggerProps = {},
}) => {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);

  /**
   * Implemented to mimic the filter on ant table columns.
   * uses a buffer to display selections but only applies once the dropdown has been closed
   */
  const [active, setActive] = useState(false);
  const [selectionBuffer, setSelectionBuffer] = useState([]);

  const clearSelections = () => {
    setSelectionBuffer([]);
  };

  const resolveSelections = () => {
    if (open) {
      setActive(!!selectionBuffer.length);
      onChange([...selectionBuffer]);
    }
    setOpen(!open);
  };

  useClickAway(dropdownRef, (event) => {
    if (!triggerRef.current.contains(event.target)) {
      if (open) {
        resolveSelections();
      }
    }
  });

  const handleSelection = (key) => {
    const index = selectionBuffer.indexOf(key);
    let bufferUpdate = [...selectionBuffer];

    if (index === -1) {
      bufferUpdate.push(key);
    } else {
      bufferUpdate.splice(index, 1);
    }
    setSelectionBuffer(bufferUpdate);
  };

  const checkboxes = items.map((item) => {
    const checked = selectionBuffer.indexOf(item.key) !== -1;
    const handleChange = () => handleSelection(item.key);
    return {
      label: (
        <Checkbox checked={checked} onChange={handleChange}>
          {item.label}
        </Checkbox>
      ),
      key: item.key,
    };
  });

  return (
    <Dropdown
      styles={{
        root: {
          boxShadow: 'var(--box-shadow)',
          borderRadius: 'var(--border-radius-lg)',
        },
      }}
      popupRender={(dropdownBody) => {
        return (
          <div
            ref={dropdownRef}
            style={{
              backgroundColor: 'var(--color-background)',
              borderRadius: 'var(--border-radius-lg)',
            }}
          >
            {dropdownBody}
            <Divider style={{ margin: 0 }} />
            <Flex
              justify="space-between"
              style={{ padding: 'var(--spacing-xs)' }}
            >
              <Button
                type="link"
                size="small"
                onClick={clearSelections}
                disabled={!selectionBuffer.length}
              >
                Reset
              </Button>
              <Button
                data-testid="submit-multiselect"
                type="primary"
                size="small"
                onClick={resolveSelections}
              >
                OK
              </Button>
            </Flex>
          </div>
        );
      }}
      trigger="click"
      open={open}
      menu={{
        items: checkboxes,
        multiple: true,
        style: {
          boxShadow: 'unset',
        },
        onClick: (item) => {
          const { domEvent, key } = item;
          domEvent?.preventDefault();
          handleSelection(key);
        },
      }}
    >
      <Trigger
        ref={triggerRef}
        active={active || undefined}
        type="link"
        onClick={resolveSelections}
        {...triggerProps}
      >
        <Icon component={FilterFilled} />
      </Trigger>
    </Dropdown>
  );
};

export default MultiSelectDropdown;
